// src/pages/PricingPage.js
import React, { useState } from 'react'; // <--- Añadir useState
import { Link, useNavigate } from 'react-router-dom';
import { Check, Crown, ArrowLeft, Sparkles, AlertCircle } from 'lucide-react'; // <--- Añadir AlertCircle

// (Opcional) Puedes crear un componente Header específico o reutilizar uno simple
const PricingHeader = () => (
    <header className="bg-gray-900/80 backdrop-blur-sm border-b border-gray-700/50 sticky top-0 z-40 mb-8 sm:mb-12">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
                <div className="flex items-center">
                   <Link to="/DesignStudio" className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-600 mr-6 flex-shrink-0"> {/* flex-shrink-0 */}
                       HomzyAI
                   </Link>
                   <h1 className="text-lg font-semibold text-white hidden sm:block truncate">Planes y Precios</h1> {/* truncate */}
                 </div>
                 <Link
                    to="/DesignStudio" // Botón para volver al dashboard
                    className="p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-700/50 transition-colors flex-shrink-0" /* flex-shrink-0 */
                    title="Volver al Estudio de Diseño"
                  >
                    <ArrowLeft size={20} />
                  </Link>
            </div>
        </nav>
    </header>
);

function PricingPage() {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false); // Estado para feedback de carga
    const [error, setError] = useState(null); // Estado para mostrar errores

    // --- Función para manejar el click en el botón Premium ---
    const handlePremiumCheckout = async () => {
        setIsLoading(true);
        setError(null); // Limpiar errores previos
        console.log("🚀 Iniciando checkout para Premium...");

        const token = localStorage.getItem('access_token');
        if (!token) {
             setError("Debes iniciar sesión para actualizar a Premium.");
             setIsLoading(false);
             // Idealmente, aquí abrirías tu AuthModal o redirigirías a /
             // navigate('/'); // O muestra el modal
             return;
        }

        // Obtener la URL base de la API desde variables de entorno o usar default
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

        try {
            // Llamada a tu backend para crear la sesión de Checkout
            const response = await fetch(`${apiUrl}/api/create-checkout-session`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}` // Enviar el token JWT
                },
                // No se necesita body si el backend obtiene el user_id del token
            });

            const data = await response.json();

            if (!response.ok) {
                console.error("Error response from backend:", data);
                // Intenta mostrar un mensaje más específico si el backend lo envía
                throw new Error(data.error?.message || `Error del servidor (${response.status})`);
            }

            if (data.redirect_url) {
                console.log("✅ Sesión creada, redirigiendo a Stripe:", data.redirect_url);
                // Redirigir al usuario a la página de pago de Stripe
                window.location.href = data.redirect_url;
                // Si la redirección funciona, el estado de isLoading no importa mucho,
                // pero si fallara por alguna razón, querrías resetearlo.
                 // setTimeout(() => setIsLoading(false), 5000); // Resetear por si acaso la redirección falla
            } else {
                 console.error("❌ No se recibió redirect_url del backend.");
                 throw new Error("No se pudo obtener la URL de pago desde el servidor.");
            }

        } catch (err) {
            console.error("❌ Error en handlePremiumCheckout:", err);
            setError(err.message || "Ocurrió un error inesperado al intentar iniciar el pago.");
            setIsLoading(false); // Asegúrate de resetear isLoading en caso de error
        }
    };

    // --- Datos de los Planes ---
    const plans = [
        {
            name: "Gratuito",
            price: "0€",
            frequency: "/ mes",
            description: "Perfecto para probar y explorar las capacidades básicas de IA.",
            features: [
                "Chat con Asistente AI",
                "Sugerencias de productos básicos",
                "Guardar productos favoritos",
                "Historial de chat limitado",
            ],
            ctaText: "Continuar Gratis",
            ctaAction: () => navigate('/DesignStudio'),
            ctaDisabled: isLoading, // Deshabilitar mientras se procesa el pago premium
            isFeatured: false,
        },
        {
            name: "Premium",
            price: "9.99€",
            frequency: "/ mes",
            description: "Desbloquea todo el potencial visual y obtén soporte prioritario.",
            features: [
                "Todo lo del plan Gratuito",
                "Generación de Rediseños Visuales con IA",
                "Resultados en alta resolución",
                "Sugerencias de productos avanzadas",
                "Historial de chat extendido",
                "Soporte prioritario",
            ],
            // 👇 --- USAR LA NUEVA FUNCIÓN Y ESTADOS ---
            ctaText: isLoading ? "Procesando..." : "Actualizar a Premium", // Mostrar estado de carga
            ctaAction: handlePremiumCheckout, // Llamar a la función async
            ctaDisabled: isLoading, // Deshabilitar botón mientras carga
            isFeatured: true,
        },
    ];

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-950 to-gray-900 text-gray-200 pb-16">
            <PricingHeader />

            <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* --- Encabezado --- */}
                <div className="text-center mb-12 sm:mb-16">
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-4">
                        <span className="bg-clip-text text-transparent bg-gradient-to-r from-white via-purple-200 to-cyan-200">
                            Elige el Plan Perfecto
                        </span>
                    </h2>
                    <p className="text-lg text-gray-400 max-w-2xl mx-auto">
                        Desde exploración gratuita hasta transformaciones visuales completas. Potencia tu creatividad con HomzyAI.
                    </p>
                </div>

                {/* --- Mensaje de Error General --- */}
                {error && (
                    <div className="mb-8 p-4 bg-red-900/50 border border-red-700 text-red-200 rounded-lg text-center flex items-center justify-center">
                         <AlertCircle size={20} className="mr-2 text-red-300 flex-shrink-0"/>
                         <span>{error}</span>
                    </div>
                )}

                {/* --- Tarjetas de Precios --- */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 items-stretch">
                    {plans.map((plan) => (
                        <div
                            key={plan.name}
                            className={`relative flex flex-col rounded-2xl border ${
                                plan.isFeatured
                                    ? 'border-purple-500/80 bg-gradient-to-br from-gray-800/60 via-gray-900/70 to-purple-900/30 shadow-purple-500/20'
                                    : 'border-gray-700/70 bg-gray-800/50'
                            } shadow-xl p-6 sm:p-8 transition-all duration-300 hover:shadow-2xl ${plan.isFeatured ? 'hover:shadow-purple-500/30' : 'hover:shadow-gray-500/10'} ${plan.ctaDisabled ? 'opacity-80' : ''}`} // Opacidad si está deshabilitado
                        >
                            {/* Badge Destacado */}
                            {plan.isFeatured && (
                                <div className="absolute top-0 right-0 mr-4 -mt-3">
                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold tracking-wide text-white bg-gradient-to-r from-purple-600 to-pink-600 shadow-md">
                                        <Crown size={12} className="mr-1 text-yellow-300" /> Recomendado
                                    </span>
                                </div>
                            )}

                            {/* Contenido de la Tarjeta */}
                            <div className="flex-1">
                                <h3 className="text-xl sm:text-2xl font-semibold text-white mb-2">{plan.name}</h3>
                                <p className="text-gray-400 text-sm mb-4">{plan.description}</p>
                                <div className="mb-6">
                                    <span className="text-3xl sm:text-4xl font-extrabold text-white">{plan.price}</span>
                                    <span className="text-base font-medium text-gray-400">{plan.frequency}</span>
                                </div>
                                <ul className="space-y-3 text-sm text-gray-300 mb-8">
                                    {plan.features.map((feature, index) => (
                                        <li key={index} className="flex items-start">
                                            <Check
                                                className={`flex-shrink-0 w-5 h-5 mr-2 ${
                                                    plan.isFeatured || feature.toLowerCase().includes("todo lo del plan gratuito")
                                                        ? 'text-purple-400'
                                                        : 'text-green-500'
                                                }`}
                                                aria-hidden="true"
                                            />
                                            <span>{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Botón CTA */}
                            <button
                                onClick={plan.ctaAction}
                                disabled={plan.ctaDisabled} // Usar estado disabled
                                className={`w-full group py-3 px-4 rounded-lg text-base font-bold relative overflow-hidden transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 ${
                                    plan.isFeatured
                                        ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 focus:ring-purple-500 disabled:opacity-60 disabled:cursor-not-allowed' // Estilo destacado y disabled
                                        : 'bg-gray-700 text-gray-200 hover:bg-gray-600 focus:ring-gray-500 disabled:opacity-60 disabled:cursor-not-allowed' // Estilo normal y disabled
                                }`}
                            >
                                <span className="relative z-10 flex items-center justify-center">
                                     {/* Mostrar icono solo si es premium y no está cargando */}
                                     {plan.isFeatured && !isLoading && <Sparkles size={16} className="mr-2 opacity-70"/> }
                                     {/* Mostrar spinner si está cargando */}
                                     {isLoading && plan.isFeatured && (
                                         <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                             <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                             <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                         </svg>
                                     )}
                                     {plan.ctaText}
                                </span>
                                {/* Efecto hover del botón destacado */}
                                {plan.isFeatured && !plan.ctaDisabled && <span className="absolute inset-0 bg-gradient-to-r from-purple-700 to-pink-700 translate-y-full group-hover:translate-y-0 transition-transform duration-300 opacity-50"></span>}
                            </button>
                        </div>
                    ))}
                </div>

                {/* --- (Opcional) Sección de FAQs --- */}
                 {/* ... (sin cambios) ... */}

            </main>
        </div>
    );
}

export default PricingPage;